import {
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import UploadIcon from '@mui/icons-material/Publish';
import { useContext, useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep } from "lodash";
import moment from "moment";
import UpdateCostCin7Dialog from "../components/UpdateCostCin7Dialog";
import Cin7CostDetailsDialog from "../components/Cin7CostDetailsDialog"
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const PAGE_SIZE = 20;
const ROW_HEIGHT = 70;

const STATUS = {
  downloading: "Retrieving data from Cin7",
  pending: "Decision Pending",
  inProgress: "Updating data in Cin7",
  completed: "Done",
  failed: "Update Failed",
  downloadFailed: "Download Failed",
}

const STATUS_COLOR = {
  downloading: "info",
  pending: "warning",
  inProgress: "info",
  completed: "success",
  failed: "error",
  downloadFailed: "error",
}

const defaultFilter = {
  date: null,
};

export default function LiquorConnect() {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [filter, setFilter] = useState(defaultFilter);

  const [products, setProducts] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [rowCount, setRowCount] = useState(0);

  const [currentRecord, setCurrentRecord] = useState({ id: null, status: "" });
  const [productsDialogVisible, setProductsDialogVisible] = useState(false);

  const goToDetailView = (id, status) => {
    setCurrentRecord({ id, status });
    setProductsDialogVisible(true);
  };

  const hideProductsDialog = (reload) => {
    setProductsDialogVisible(false);
    setCurrentRecord({ id: null, status: "" });
    if (reload) {
      loadProducts(true);
    }
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Created at",
      width: 250,
      sortable: false,
      valueGetter: (value) => value
          ? format(new Date(value), "yyyy-MM-dd hh:mm a")
          : ""
    },
    {
      field: "status",
      headerName: "Status",
      width: 250,
      sortable: false,
      renderCell: (params) => {
        return (
          <Chip
            label={STATUS[params.value]}
            size="small"
            variant="filled"
            color={STATUS_COLOR[params.value]} 
          />
        );
      }
    },
    {
      field: "id",
      headerName: "Detail",
      width: 400,
      sortable: false,
      renderCell: (params) => {
        return (
          <Button color="primary" onClick={() => goToDetailView(params?.row?.id, params?.row?.status)}>View details</Button>
        );
      },
    },
  ];

  const showDialog = () => setDialogVisible(true);
  const hideDialog = (reload) => {
    setDialogVisible(false);
    if (reload) {
      loadProducts(true);
    }
  }

  const handleReset = () => setFilter(cloneDeep(defaultFilter))

  let active = useRef(false);

  const loadProducts = async (reset) => {
    setIsLoading(true);
    let filters = {
      limit: PAGE_SIZE,
      offset: reset ? 0 : paginationModel.page * PAGE_SIZE,
    };
    if (filter.date) {
      filters.dateFrom = moment(filter.date).startOf('day').format();
      filters.dateTo = moment(filter.date).endOf('day').format();
    }
    const params = new URLSearchParams(filters);
    const response = await fetchWithInterceptor(
      `/api/inventory-management/cin7-cost-upload?${params}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      }
    );
    if (response.ok) {
      if (!active.current) {
        return;
      }
      const data = await response.json();
      if (reset) {
        setPaginationModel({ ...paginationModel, page: 0 });
      }
      setProducts(data.logs);
      setRowCount(data.total);
    } else {
      console.log(response);
      appContext.showMessage(
        "There was an error getting the products",
        "error"
      );
    }
    setIsLoading(false);
  };

  useEffect(() => {
    active.current = true;

    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    loadProducts(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadProducts(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Upload the AGLC "Product Price List and Details - F66074" file to get a filterable list of all current product costs. You can then choose whether or not to automatically apply these cost changes to Cin7 and Shopify.
      </Typography>
      <Container component="main" maxWidth="100%" sx={{ pb: 3 }}>
        <Paper sx={{ width: "100%", bgcolor: "background.paper", mt: 2, mb: 2 }}>
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Update Costs (Cin7)</Typography>
            <br />
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Uploaded at"
                  value={filter.date}
                  onChange={(date) => setFilter({ ...filter, date })}
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: "auto" }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleReset}>
                  RESET FILTERS
                </Button>
              </Grid>
            </Grid>
          </div>
          <Divider />

          <Grid container justifyContent="flex-end" style={{ padding: 16 }} spacing={1}>
            <Grid>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={showDialog}
                startIcon={<UploadIcon />}
              >
                Upload CSV
              </Button>
            </Grid>
          </Grid>

          <DataGrid
            rows={products}
            columns={columns}
            pageSize={PAGE_SIZE}
            pageSizeOptions={[PAGE_SIZE]}
            disableRowSelectionOnClick
            disableColumnFilter
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            rowCount={rowCount}
            loading={isLoading}
            rowHeight={ROW_HEIGHT}
            sx={{
              height: ROW_HEIGHT * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": {
                display: "none",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
                whiteSpace: "wrap",
                lineHeight: 1,
              },
              "& .MuiDataGrid-cell--withRenderer": {
                display: "block",
                textOverflow: "unset",
              },
              "& .MuiDataGrid-cell--withRenderer span": {
                overflow: "hidden",
                textWrap: "nowrap",
                textOverflow: "ellipsis",
                display: "block",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
                justifyContent: "center",
              },
              "& .MuiChip-root.a_error": {
                color: "rgb(97, 26, 21)",
                backgroundColor: "rgb(253, 236, 234)",
              },
              "& .MuiChip-root.b_modified": {
                color: "rgb(30, 70, 32)",
                backgroundColor: "rgb(237, 247, 237)",
              },
              "& .MuiChip-root.c_unmodified": {
                color: "rgb(13, 60, 97)",
                backgroundColor: "rgb(232, 244, 253)",
              },
            }}
          />
        </Paper>
        <UpdateCostCin7Dialog open={dialogVisible} onClose={hideDialog} />
        <Cin7CostDetailsDialog id={currentRecord.id} status={currentRecord.status} open={productsDialogVisible} onClose={hideProductsDialog} />
      </Container>
    </>
  );
}

